import type { PayloadAction } from '@reduxjs/toolkit';
import { logger } from 'lib/logger';
import { BillingInfoAPICall } from 'reactApp/api/billing/BillingInfoAPICall';
import { SendPaymentLetterAPICall } from 'reactApp/api/billing/SendPaymentLetterAPICall';
import { billingDataForPaidInfoEnabled } from 'reactApp/appHelpers/featuresHelpers/features/billingDataEndpoint';
import { billingDataFailure, getBillingDataRequest, getBillingDataSuccess } from 'reactApp/modules/billingData/billingData.module';
import {
    loadPaidInfoFailure,
    loadPaidInfoRequest,
    loadPaidInfoSuccess,
    sendPaymentLetter as sendPaymentLetterAction,
} from 'reactApp/modules/paidInfo/paidInfo.module';
import type { ApiPaidInfoResponse } from 'reactApp/modules/paidInfo/paidInfo.types';
import { getProductById } from 'reactApp/modules/products/products.selectors';
import { UserSelectors } from 'reactApp/modules/user/user.selectors';
import { call, cancel, put, race, select, take, takeEvery } from 'redux-saga/effects';

import { PaidInfoSelectors } from './paidInfo.selectors';

const sendPaymentLetterApiCall = ({ id, name, space }) =>
    new SendPaymentLetterAPICall().makeRequest({ product_id: id, product_name: name, product_space: space });

function* getPaidInfo() {
    const isUserWithBilling = yield select(UserSelectors.isUserWithBilling);
    if (!isUserWithBilling) {
        yield cancel();
    }

    try {
        const { data } = yield call(new BillingInfoAPICall().makeRequest);
        yield put(loadPaidInfoSuccess(data as ApiPaidInfoResponse));
    } catch (error) {
        yield put(loadPaidInfoFailure());
        yield cancel();
    }
}

export function* loadPaidInfo() {
    if (billingDataForPaidInfoEnabled) {
        yield put(getBillingDataRequest({ radar: 'paidinfo' }));
    } else {
        yield getPaidInfo();
    }
}

export function* handlePaidInfoSuccess() {
    const { isLoaded, isLoading } = yield select(PaidInfoSelectors.getLoadingState);
    if (!isLoaded || isLoading) {
        if (billingDataForPaidInfoEnabled) {
            yield race({
                success: take(getBillingDataSuccess.toString()),
                fail: take(billingDataFailure.toString()),
            });
        } else {
            yield race({
                success: take(loadPaidInfoSuccess.toString()),
                fail: take(loadPaidInfoFailure.toString()),
            });
        }
    }
}

function* sendPaymentLetter(action: PayloadAction<{ id: string }>) {
    try {
        const { id } = action.payload;
        const product = yield select(getProductById, id);

        if (!product) {
            return;
        }

        yield sendPaymentLetterApiCall({ id: product.id, space: product.space.original, name: product.space.value });
    } catch (error) {
        logger.error(error);
        yield cancel();
    }
}

export function* watchPaidInfo() {
    yield takeEvery(loadPaidInfoRequest.toString(), loadPaidInfo);
    yield takeEvery(sendPaymentLetterAction.toString(), sendPaymentLetter);
}
